import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Grid,
  InputAdornment,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import './Login.css';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 🔑 2FA States
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorInfoMsg, setTwoFactorInfoMsg] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const navigate = useNavigate();

  // ⏱️ Resend cooldown timer
  useEffect(() => {
    let timer: any;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('🔐 Login attempt:', formData.email);
      const response = await authService.login(formData.email, formData.password);
      console.log('🔐 Login response:', response);

      if (response.requires2FA) {
        setRequires2FA(true);
        setTempToken(response.tempToken || '');
        setTwoFactorInfoMsg(response.message || 'Giriş doğrulama kodu e-postanıza gönderildi.');
        setResendCooldown(60);
        return;
      }
      
      if (response.success && response.data) {
        console.log('✅ Login successful, navigating to dashboard');
        window.location.href = '/';
      } else {
        console.error('❌ Login failed:', response.message);
        setError(response.message || 'Giriş başarısız');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setError('Giriş sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.trim().length !== 6) {
      setError('Lütfen e-postanıza gelen 6 haneli doğrulama kodunu girin.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authService.verify2FA(tempToken, twoFactorCode.trim());
      if (response.success && response.data) {
        console.log('✅ 2FA Verified successfully, entering system');
        window.location.href = '/';
      } else {
        setError(response.message || 'Doğrulama kodu geçersiz');
      }
    } catch (err: any) {
      setError('Doğrulama sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    setError('');
    try {
      const res = await authService.resend2FA(tempToken);
      if (res.success) {
        setTwoFactorInfoMsg(res.message || 'Yeni doğrulama kodu e-postanıza tekrar gönderildi.');
        setResendCooldown(60);
      } else {
        setError(res.message || 'Kod tekrar gönderilemedi');
      }
    } catch (err) {
      setError('Kod tekrar gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        fontFamily: 'Axiforma, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        overflow: 'hidden',
        backgroundImage: 'url(https://images.unsplash.com/photo-1634712282287-14ed57b9cc89?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YXJtY2hhaXJ8ZW58MHx8MHx8fDA%3D)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1,
        },
      }}
    >
      <Grid container sx={{ height: '100%', overflow: 'hidden', position: 'relative', zIndex: 2 }}>
        {/* Sol Panel - Illustration */}
        <Grid component="div" size={{ xs: 12, md: 7 }} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px',
              position: 'relative',
            }}
          >
            {/* Decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                right: '15%',
                width: '400px',
                height: '400px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                filter: 'blur(60px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '10%',
                left: '10%',
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.15)',
                filter: 'blur(50px)',
              }}
            />
            
            {/* Content */}
            <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/percrm-logo.svg`}
                alt="PerCRM Logo"
                onError={(e: any) => { 
                  console.error('Logo yüklenemedi:', e.target.src);
                  e.target.onerror = null;
                  e.target.src = `${process.env.PUBLIC_URL}/logo.svg`;
                }}
                sx={{
                  width: '100%',
                  maxWidth: '220px',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto 24px',
                  filter: 'drop-shadow(0 4px 20px rgba(0, 0, 0, 0.3))',
                }}
              />
              
              <Typography
                sx={{
                  fontSize: '18px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.8,
                  maxWidth: '500px',
                  margin: '0 auto',
                  textShadow: '0 1px 10px rgba(0, 0, 0, 0.1)',
                }}
              >
                Yeni nesil çözüm merkezi platformu ile müşteri deneyiminizi
                en üst seviyeye taşıyın.
              </Typography>

              {/* Feature badges */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  marginTop: '48px',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                {['Akıllı Yönlendirme', 'Gerçek Zamanlı Raporlar', 'Kolay Entegrasyon'].map((feature) => (
                  <Box
                    key={feature}
                    sx={{
                      padding: '12px 24px',
                      borderRadius: '24px',
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {feature}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Sağ Panel - Login Form */}
        <Grid component="div" size={{ xs: 12, md: 5 }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '480px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                borderRadius: '30px',
                padding: '48px',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37), inset 0 0 0 1px rgba(255, 255, 255, 0.18)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                borderTop: '1px solid rgba(255, 255, 255, 0.4)',
                borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '30px',
                  padding: '2px',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 100%)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  pointerEvents: 'none',
                },
              }}
            >
              {/* Title */}
              <Typography
                sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '32px',
                  textAlign: 'center',
                  marginBottom: '12px',
                  marginTop: '8px',
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                }}
              >
                {requires2FA ? '🔑 Doğrulama Kodu' : 'Hoş Geldiniz'}
              </Typography>

              {/* Subtitle */}
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  textAlign: 'center',
                  marginBottom: '28px',
                  lineHeight: 1.6,
                  textShadow: '0 1px 5px rgba(0, 0, 0, 0.2)',
                }}
              >
                {requires2FA 
                  ? `${formData.email} adresinize gönderilen 6 haneli kodu giriniz.`
                  : 'Devam etmek için giriş yapın'}
              </Typography>

              {twoFactorInfoMsg && requires2FA && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 3, 
                    borderRadius: '12px',
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    color: '#ffffff',
                    border: '1px solid rgba(147, 197, 253, 0.4)',
                    backdropFilter: 'blur(10px)',
                    '& .MuiAlert-icon': { color: '#93C5FD' },
                    '& .MuiAlert-message': { fontSize: '0.88rem' }
                  }}
                >
                  {twoFactorInfoMsg}
                </Alert>
              )}

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3, 
                    borderRadius: '12px',
                    backgroundColor: '#FEE2E2',
                    color: '#DC2626',
                    border: '1px solid #FCA5A5',
                    '& .MuiAlert-icon': {
                      color: '#DC2626'
                    },
                    '& .MuiAlert-message': {
                      fontSize: '0.9rem'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}

              {requires2FA ? (
                /* 🔑 2FA Verification Form */
                <form onSubmit={handleVerify2FA}>
                  <Box sx={{ marginBottom: '24px' }}>
                    <Typography 
                      sx={{ 
                        fontSize: '14px', 
                        fontWeight: 500, 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        mb: 1,
                        ml: 0.5,
                        textAlign: 'center'
                      }}
                    >
                      6 Haneli Giriş Kodu
                    </Typography>
                    <TextField
                      fullWidth
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                      required
                      autoFocus
                      placeholder="123456"
                      inputProps={{
                        maxLength: 6,
                        style: {
                          textAlign: 'center',
                          letterSpacing: '10px',
                          fontSize: '24px',
                          fontWeight: '700',
                          fontFamily: 'monospace'
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '64px',
                          borderRadius: '14px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)',
                          color: '#ffffff',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            borderWidth: '2px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#ffffff',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#ffffff',
                            borderWidth: '2px',
                          },
                        },
                      }}
                    />
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    disabled={loading || twoFactorCode.length !== 6}
                    sx={{
                      height: '56px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      fontSize: '16px',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#fff',
                      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
                        boxShadow: '0 6px 20px rgba(16, 185, 129, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                      marginBottom: '16px',
                    }}
                  >
                    {loading ? 'Doğrulanıyor...' : 'Doğrula ve Giriş Yap'}
                  </Button>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Button
                      type="button"
                      onClick={handleResend2FA}
                      disabled={loading || resendCooldown > 0}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '13px',
                        textTransform: 'none',
                        '&:hover': { color: '#ffffff' }
                      }}
                    >
                      {resendCooldown > 0 ? `Yeniden Gönder (${resendCooldown}s)` : 'Kodu Yeniden Gönder'}
                    </Button>

                    <Button
                      type="button"
                      onClick={() => {
                        setRequires2FA(false);
                        setTwoFactorCode('');
                        setError('');
                      }}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '13px',
                        textTransform: 'none',
                        '&:hover': { color: '#ffffff' }
                      }}
                    >
                      ← İptal / Geri
                    </Button>
                  </Box>
                </form>
              ) : (
                <form onSubmit={handleSubmit}>
                {/* Email Field */}
                <Box sx={{ marginBottom: '20px' }}>
                  <Typography 
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 500, 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      mb: 1,
                      ml: 0.5
                    }}
                  >
                    E-posta
                  </Typography>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    autoFocus
                    placeholder="ornek@email.com"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '56px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        color: '#ffffff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          borderWidth: '2px',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.25)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.7)',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 18px',
                        fontSize: '15px',
                        color: '#ffffff',
                        '&::placeholder': {
                          color: 'rgba(255, 255, 255, 0.5)',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>

                {/* Password Field */}
                <Box sx={{ marginBottom: '16px' }}>
                  <Typography 
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 500, 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      mb: 1,
                      ml: 0.5
                    }}
                  >
                    Parola
                  </Typography>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              color: 'rgba(255, 255, 255, 0.9)',
                              padding: '0 8px',
                              transition: 'color 0.3s ease',
                              '&:hover': {
                                color: '#ffffff',
                              },
                            }}
                          >
                            {showPassword ? 'Gizle' : 'Göster'}
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '56px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        color: '#ffffff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          borderWidth: '2px',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.25)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.7)',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 18px',
                        fontSize: '15px',
                        color: '#ffffff',
                        '&::placeholder': {
                          color: 'rgba(255, 255, 255, 0.5)',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>

                {/* Forgot Password Link */}
                <Box sx={{ marginBottom: '28px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Link
                    href="#"
                    sx={{
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      fontWeight: 500,
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: '#ffffff',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Şifremi Unuttum
                  </Link>
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  disabled={loading || !formData.email || !formData.password}
                  sx={{
                    height: '56px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontSize: '16px',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#fff',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    '&:disabled': {
                      background: '#E5E7EB',
                      color: '#9CA3AF',
                      boxShadow: 'none',
                    },
                    marginBottom: '24px',
                    marginTop: '8px',
                  }}
                >
                  {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                </Button>
              </form>
              )}

              {/* Signup Link */}
              <Box
                sx={{
                  textAlign: 'center',
                  width: '100%',
                  marginTop: '24px',
                  fontSize: '14px',
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    marginRight: '4px',
                  }}
                >
                  Hesabınız yok mu?
                </Typography>
                <Link
                  href="/signup"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 600,
                    textDecoration: 'none',
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Kayıt Olun
                </Link>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;